import { useQuery } from '@tanstack/react-query';
import { createBffClient } from '@/lib/bff/create-storefront-client';
import { usePricingContext } from '@/lib/hooks/use-pricing-context';
import { setCachedCollections, setSaleCachedCollection, isNavCached, getCachedSaleCollection } from '@/lib/hooks/use-nav-collections';
import type {
  SearchFilters,
  CollectionWithProducts,
  CollectionEntity,
  CollectionPack,
} from '@/domain/entities/collection.entity';
import type { CollectionPageResponse, CollectionDto, CollectionPack as CollectionPackDto } from '@template-project/storefront-bff-client';

function toNavCollection(c: CollectionDto) {
  return { label: c.title, slug: c.slug, imageUri: c.imageUri };
}

function buildStableFiltersKey(filters: SearchFilters): string {
  return JSON.stringify(
    Object.keys(filters)
      .sort((a, b) => a.localeCompare(b))
      .reduce<Record<string, unknown>>((acc, k) => {
        acc[k] = filters[k as keyof SearchFilters] as unknown;
        return acc;
      }, {}),
  );
}

function mapDtoToEntity(c: CollectionDto): CollectionEntity {
  return {
    id: c.id,
    title: c.title,
    description: c.description,
    slug: c.slug,
    type: c.type,
    status: c.status,
    imageUri: c.imageUri,
    sortOrder: c.sortOrder,
    showInMenu: c.showInMenu,
    menuPosition: c.menuPosition,
    productCount: c.productCount,
    packCount: c.packCount,
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
  };
}

function mapPackDto(p: CollectionPackDto): CollectionPack {
  return {
    id: p.id,
    name: p.name,
    slug: p.slug,
    status: p.status,
    imageUri: p.imageUri,
    packType: p.packType,
    priceMin: p.priceMin,
    compareAt: p.compareAt,
  };
}

export interface CollectionPageData extends CollectionWithProducts {
  nav: {
    menuCollections: CollectionEntity[];
    saleCollection: { slug: string; title: string } | null;
  };
}

/**
 * Fetches the collection detail page bundle from GET /v1/storefront/pages/collection/:slug.
 *
 * Initial load (nav not yet in module cache):
 *   Uses the full bundle endpoint — returns collection + products + facets + nav in one request.
 *   Seeds menu collections and sale collection into the module-level nav store.
 *
 * Subsequent filter/sort/paginate interactions (nav already cached):
 *   Uses GET /v1/storefront/collections/:slug — collection data only, no nav re-fetch.
 *   Nav is already seeded; Header and CategoryNav read from the module store directly.
 */
export function useCollectionPage(slug: string, filters: SearchFilters = {}) {
  const filtersKey = buildStableFiltersKey(filters);
  const { currency, query: pricingQuery } = usePricingContext();

  return useQuery({
    queryKey: ['collection-page', slug, filtersKey, currency],
    queryFn: async (): Promise<CollectionPageData> => {
      const client = createBffClient();
      const collectionFilters = { ...filters, ...pricingQuery };

      if (!isNavCached()) {
        // Cold load — fetch full bundle (collection + nav)
        const res: CollectionPageResponse = await client.getCollectionPage(slug, collectionFilters);

        if (res.data.nav.menuCollections.length > 0) {
          setCachedCollections(res.data.nav.menuCollections.map(toNavCollection));
        }
        setSaleCachedCollection(res.data.nav.saleCollection);

        return {
          collection: mapDtoToEntity(res.data.collection),
          products: res.data.products,
          packs: (res.data.packs ?? []).map(mapPackDto),
          facets: res.data.facets,
          pagination: res.meta.pagination,
          nav: {
            menuCollections: res.data.nav.menuCollections.map(mapDtoToEntity),
            saleCollection: res.data.nav.saleCollection,
          },
        };
      }

      // Nav already cached — fetch collection data only, skip nav upstream calls
      const res = await client.getCollection(slug, collectionFilters);

      return {
        collection: mapDtoToEntity(res.data.collection),
        products: res.data.products,
        packs: (res.data.packs ?? []).map(mapPackDto),
        facets: res.data.facets,
        pagination: res.meta.pagination,
        nav: {
          // Not consumed by CollectionDetailPage — Header/CategoryNav read from module store
          menuCollections: [],
          saleCollection: getCachedSaleCollection(),
        },
      };
    },
    enabled: !!slug,
    staleTime: 60 * 1000,
    retry: 1,
  });
}
